import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Logo } from '../common/Logo';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLogout } from '../../hooks/useLogout';
import AddJobModal from '../features/dashboard/jobs/AddJobModal';
import PurpleButton from '../common/PurpleButton';
import ThemeToggleButton from '../ui/ThemeToggleButton';
import { useTheme } from '../../context/ThemeContext';
import { Dialog, DialogPanel, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';

// List of admin user emails - in production, this should be stored in Firestore
const ADMIN_EMAILS = ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'];

const Navbar = ({ banner, forCandidates, showProfileMenu = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { user, userProfile } = useAuthContext();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const buttonRef = useRef(null);
  
  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown) {
        const target = event.target;
        const isDropdownClick = target.closest('[data-dropdown]');
        if (!isDropdownClick) {
          setShowDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);
  
  // Determine which link to show based on current route and user authentication status
  const getCurrentNavigation = () => {
    if (user) {
      return [];
    }
    
    // Check if we're on any employer-related page
    if (location.pathname === '/employer-landing' || location.pathname === '/employers') {
      return [{ href: '/', label: 'For Candidates' }];
    }
    
    // Default: show "For Employers" button (for main landing page and other pages)
    return [{ href: '/employer-landing', label: 'For Employers', isEmployerButton: true }];
  };
  
  const links = getCurrentNavigation();

  // Check if current user is an admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/');
      window.scrollTo(0, 0);
    }
  };

  const handleLogout = async () => {
    // Navigate to landing page first to avoid race conditions with protected routes
    navigate('/');
    setShowDropdown(false);
    // Then perform the logout
    await logout();
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };
  
  return (
    <header className="relative py-2 px-4 sm:px-6">
      <div className="relative flex justify-between items-center">
        <div className="relative flex gap-6">
          {/* Logo section - hide on desktop when DesktopSidebar is present (authenticated users) */}
          <div className={`py-3 ${user ? 'block md:hidden' : 'block'}`}>
            <a href="#" onClick={handleLogoClick} title="Home" className="flex items-center gap-2">
              <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
            </a>
          </div>
          {banner && (
            <div className="relative hidden items-center py-3 lg:flex">
              {banner}
            </div>
          )}
        </div>
        
        <div className="flex items-center">
          {/* Desktop Navigation */}
          <nav className="relative hidden lg:flex items-center">
            {links.map(({ href, label, onClick }) => (
              <div key={href} className="relative flex">
                {onClick ? (
                  <PurpleButton
                    onClick={onClick}
                    withIcon
                  >
                    {label}
                  </PurpleButton>
                ) : (
                  <a
                    href={href}
                    className={`flex items-center px-4 py-3 text-base font-medium ${
                      href === '/employer-landing' ? 'text-gray-950 hover:bg-black/5 font-semibold' : 
                      'text-gray-950 hover:bg-black/5 dark:text-gray-100 dark:hover:bg-white/5'}`}
                  >
                    {label}
                  </a>
                )}
              </div>
            ))}
          </nav>
          
          {/* User Profile Menu */}
          {showProfileMenu && user && (
            <div className="relative ml-4 hidden lg:block" data-dropdown>
              <div 
                onClick={toggleDropdown}
                className="cursor-pointer"
              >
                {(() => {
                  // Priority order for profile picture:
                  // 1. Custom uploaded profile picture from Firestore (userProfile.profilePicture)
                  // 2. Profile picture from basicInfo in Firestore (userProfile.basicInfo?.photoURL)
                  // 3. Firebase Auth profile picture (user.photoURL)
                  const profilePictureUrl = userProfile?.profilePicture || 
                                          userProfile?.basicInfo?.photoURL || 
                                          user.photoURL;
                  
                  return profilePictureUrl ? (
                    <img 
                      src={profilePictureUrl} 
                      alt={`${user.displayName || 'User'}'s profile`} 
                      className="h-12 w-12 rounded-full object-cover border-2 border-purple-500 hover:border-purple-600 transition-all"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center border-2 border-purple-500 hover:border-purple-600 transition-all">
                      <span className="text-purple-600 dark:text-purple-400 text-lg font-bold">
                        {(user.displayName || 'U').charAt(0)}
                      </span>
                    </div>
                  );
                })()}
              </div>
              
              {showDropdown && (
                <div className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{user.displayName || 'User'}</div>
                      <div className="text-xs text-gray-500 truncate dark:text-gray-400">{user.email}</div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setShowDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        Admin Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate('/dashboard');
                        setShowDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Dashboard
                    </button>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setShowDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      My Profile
                    </button>
                    <button
                      onClick={toggleTheme}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex justify-between items-center"
                    >
                      <span>Toggle Theme</span>
                      <div className="flex items-center justify-center">
                        {isDarkMode ? (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="currentColor" 
                            className="w-5 h-5"
                          >
                            <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                          </svg>
                        ) : (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="currentColor" 
                            className="w-5 h-5"
                          >
                            <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
        </div>
      </div>
      


      {/* Add Job Modal */}
      <AddJobModal 
        isOpen={isAddJobModalOpen} 
        onClose={() => setIsAddJobModalOpen(false)} 
        user={user}
      />

      {/* Contact Founder Modal */}
      <Transition appear show={isContactModalOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => setIsContactModalOpen(false)}>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <DialogPanel className={`w-full max-w-2xl transform overflow-hidden rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} text-left align-middle shadow-xl transition-all relative`}>
                {/* Close button */}
                <button
                  onClick={() => setIsContactModalOpen(false)}
                  className={`absolute top-4 right-4 z-10 p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>

                {/* Modal content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
                  <div className={`uppercase text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} tracking-wider mb-1`}>CONTACT US</div>
                  <h2 className={`text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Connect with Burnt</h2>
                  
                  {/* Founder contact section */}
                  <div className={`flex items-center mb-6 p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                    <img 
                      src="/img/T08KWPR8PRT-U08KSBV3T8E-f64bf43096f9-512.jpeg" 
                      alt="Grey Weissend" 
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h3 className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Grey Weissend</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>CTO and Co-Founder</p>
                      <a 
                        href="mailto:grey@justburnt.com" 
                        className={`text-sm ${isDarkMode ? 'text-gray-200 hover:text-white' : 'text-gray-900 hover:text-gray-700'} flex items-center mt-1`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        grey@justburnt.com
                      </a>
                      <a 
                        href="https://www.linkedin.com/in/greyweissend/"
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`text-sm ${isDarkMode ? 'text-gray-200 hover:text-white' : 'text-gray-900 hover:text-gray-700'} flex items-center mt-1`}
                      >
                        <svg fill="currentColor" viewBox="0 0 20 20" className="h-4 w-4 mr-1">
                          <path
                            d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                            clipRule="evenodd"
                            fillRule="evenodd"
                          />
                        </svg>
                        LinkedIn
                      </a>
                    </div>
                  </div>
                  
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                    Connect with a founder and discuss your job search strategy. Get personalized guidance and help design what's next.
                  </p>
                  
                  {/* Calendar - iframe for desktop, direct link for mobile */}
                  <div className="calendar-wrapper">
                    {/* Desktop: Embedded calendar */}
                    <div className="hidden md:block">
                      <iframe
                        src={`https://cal.com/grey-weissend/30min?embed=true&theme=${isDarkMode ? 'dark' : 'light'}`}
                        width="100%"
                        height="500"
                        frameBorder="0"
                        style={{ 
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                        title="Schedule a call with Grey"
                      />
                    </div>
                    
                    {/* Mobile: Direct link button */}
                    <div className="block md:hidden">
                      <div className={`text-center p-8 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                        <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                          For the best mobile experience, we'll open the calendar in a new tab.
                        </p>
                        <a
                          href="https://cal.com/grey-weissend/30min"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-200"
                          onClick={() => setIsContactModalOpen(false)}
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v14a2 2 0 002 2z" />
                          </svg>
                          Schedule a Call
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
              </div>
            </div>
          </Dialog>
        </Transition>
    </header>
  );
};

export default Navbar;